/**
 * useWorkspaces — shared workspace-context state for the coherent shell.
 *
 * Relocated from WorkspaceSwitcher (Track A). Both the Sidebar (for IA
 * scoping) and the ProfileMenu (for the Workspace switcher section) read the
 * same list, so the `/api/me/workspaces` fetch + `/api/me/active-context` POST
 * live here once.
 *
 * Endpoints reused verbatim from the Track A WorkspaceSwitcher:
 *   GET  /api/me/workspaces        — list personal + companies + admin
 *   POST /api/me/active-context    — persist the active context server-side
 *
 * No `any` (Inv 13). No console.* (Inv 12).
 */

import { useEffect, useState } from 'react';
import { apiFetch } from '@/lib/api-client';

export type WorkspaceType = 'personal' | 'company' | 'admin';

export interface Workspace {
  /** Stable id: 'personal' | `company-<id>` | 'admin'. */
  id: string;
  name: string;
  type: WorkspaceType;
  /** Root route the workspace lands on. */
  rootRoute: string;
  /** Display role label (company workspaces only). */
  role?: string;
}

interface WorkspacesResponse {
  ok: boolean;
  data: {
    personal: { user_id: string; display_name: string };
    companies: Array<{
      company_id: string;
      company_name: string;
      role: string;
      role_display: string;
    }>;
    system_admin: boolean;
    active_context: { kind: string; company_id?: string };
  };
}

export interface UseWorkspacesResult {
  workspaces: Workspace[];
  /** Persist the new active context, then run `onDone` (typically navigate + refresh). */
  selectWorkspace: (ws: Workspace, onDone: () => void) => void;
}

/**
 * Persist the active context server-side BEFORE the caller navigates (Track A
 * Fix 2). Navigation happens via `onDone` only after the POST settles so the
 * server-side scope is set before any route-triggered request fires.
 */
export function postActiveContext(ws: Workspace, onDone: () => void): void {
  const body: { kind: string; company_id?: string } = {
    kind: ws.type === 'admin' ? 'admin' : ws.type,
  };
  if (ws.type === 'company') {
    const match = ws.id.match(/^company-(.+)$/);
    body.company_id = match?.[1] ?? '';
  }

  fetch('/api/me/active-context', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  })
    .then(() => {
      onDone();
    })
    .catch(() => {
      // On POST failure, run onDone anyway so the UI is not stuck.
      onDone();
    });
}

export function useWorkspaces(): UseWorkspacesResult {
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);

  useEffect(() => {
    async function loadWorkspaces(): Promise<void> {
      try {
        const res = await apiFetch('/api/me/workspaces', { credentials: 'same-origin' });
        if (!res.ok) return;
        const data = (await res.json()) as WorkspacesResponse;
        if (!data.ok) return;

        const ws: Workspace[] = [];

        ws.push({
          id: 'personal',
          name: 'Personal',
          type: 'personal',
          // /me/data is the personal workspace landing: accessible to all users
          // (no role-redirect guard), shows personal data-download page.
          // /dashboard is guarded by DashboardGuard which redirects system admins
          // away, so we avoid it as the switcher target.
          rootRoute: '/me/data',
        });

        for (const company of data.data.companies) {
          ws.push({
            id: `company-${company.company_id}`,
            name: company.company_name,
            type: 'company',
            rootRoute: `/company/${company.company_id}/dashboard`,
            role: company.role_display,
          });
        }

        if (data.data.system_admin) {
          ws.push({
            id: 'admin',
            name: 'System Admin',
            type: 'admin',
            rootRoute: '/admin',
          });
        }

        setWorkspaces(ws);
      } catch {
        // On error, keep defaults (empty list).
      }
    }

    void loadWorkspaces();
  }, []);

  return {
    workspaces,
    selectWorkspace: postActiveContext,
  };
}
